import { useEffect, useState } from "react";
import { Check, Loader2, Pencil, X } from "lucide-react";

/**
 * Edit a report's target where the target is read.
 *
 * The new business target moves with the plan, so burying it in the report
 * definition editor would make the number that changes most often the hardest
 * one to change. This writes straight to the definition's display.target, so
 * the board pack, the dashboard and the builder all pick it up.
 */
export default function TargetEditor({ target, onSave, formatValue }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(target ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Re-sync when the report reloads with a target changed elsewhere.
  useEffect(() => {
    if (!editing) setDraft(target ?? "");
  }, [target, editing]);

  const format = formatValue || ((v) => new Intl.NumberFormat("en-GB").format(v));

  const commit = async () => {
    const next = draft === "" ? null : Number(draft);
    if (next !== null && Number.isNaN(next)) {
      setError("Enter a number");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave(next);
      setEditing(false);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => setEditing(true)}
        className="group inline-flex items-center gap-1.5 rounded-full border border-transparent px-2 py-1 text-right hover:border-[#cccccc] hover:bg-[#f5f5f5] print:hover:border-transparent"
        title="Edit target"
        data-testid="edit-target"
      >
        <span>
          <span className="block font-sans text-sm font-bold leading-none text-[#323c44]">
            {target === null || target === undefined ? "Not set" : format(target)}
          </span>
          <span className="mt-1 block text-[10px] font-semibold uppercase tracking-[0.16em] text-[#4a5b68]">
            Target
          </span>
        </span>
        <Pencil className="h-3 w-3 text-[#a9b3bb] group-hover:text-[#119996] print:hidden" />
      </button>
    );
  }

  return (
    <div className="flex items-center gap-1 print:hidden">
      <input
        autoFocus
        type="number"
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter") commit();
          if (event.key === "Escape") {
            setEditing(false);
            setDraft(target ?? "");
            setError(null);
          }
        }}
        className="w-28 rounded border border-[#cccccc] px-2 py-1 text-sm text-[#323c44] focus:border-[#57c1a9] focus:outline-none"
        aria-label="Target"
      />
      <button
        type="button"
        onClick={commit}
        disabled={saving}
        className="rounded p-1 text-emerald-600 hover:bg-emerald-50 disabled:opacity-50"
        title="Save target"
      >
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
      </button>
      <button
        type="button"
        onClick={() => {
          setEditing(false);
          setDraft(target ?? "");
          setError(null);
        }}
        className="rounded p-1 text-[#7d8b96] hover:bg-slate-100"
        title="Cancel"
      >
        <X className="h-4 w-4" />
      </button>
      {error && <span className="text-xs text-red-600">{error}</span>}
    </div>
  );
}
